// External Dependencies
import { Fragment, useMemo } from 'react';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import { ListItemSecondaryAction } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ListAdminOrganizationsResponseItem } from '@presto-assistant/api_types/api/admin/organizations';

// Local Dependencies
import DistrictOrganizationList, { type DistrictGroup } from './DistrictOrganizationList';

// Types
interface OrganizationListProps {
  expandedDistricts: Set<string>;
  handleToggleDistrict: (districtLabel: string) => void;
  organizations: ListAdminOrganizationsResponseItem[];
}

// Component Definition
const OrganizationList = ({
  expandedDistricts,
  handleToggleDistrict,
  organizations,
}: OrganizationListProps): JSX.Element => {
  // Group organizations by admin portal
  const organizationsByAdminPortal = useMemo(() => {
    if (!organizations.length) {
      return [];
    }

    // Group organizations by admin portal
    const grouped = organizations.reduce((acc, org) => {
      const districtLabel = org.districtLabel ?? 'No Admin Portal';

      acc[districtLabel] ??= {
        districtLabel,
        organizations: [],
      };

      acc[districtLabel].organizations.push(org);

      return acc;
    }, {} as Record<
      string,
      DistrictGroup
    >);

    // Convert to array and sort by admin portal label
    return Object.values(grouped).sort((a, b) => {
      // Put "No Admin Portal" at the end
      if (a.districtLabel === 'No Admin Portal') {
        return 1;
      }

      if (b.districtLabel === 'No Admin Portal') {
        return -1;
      }

      return a.districtLabel.localeCompare(b.districtLabel);
    });
  }, [organizations]);

  return (
    <List dense>
      {organizationsByAdminPortal.map((adminPortalGroup) => {
        const isExpanded = expandedDistricts.has(adminPortalGroup.districtLabel);
        const orgCount = adminPortalGroup.organizations.length;

        return (
          <Fragment key={adminPortalGroup.districtLabel}>
            <ListItemButton
              onClick={() => handleToggleDistrict(adminPortalGroup.districtLabel)}
            >
              <ListItemText
                primary={adminPortalGroup.districtLabel}
                secondary={`${orgCount} organization${orgCount !== 1 ? 's' : ''}`}
              />

              <ListItemSecondaryAction>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemSecondaryAction>
            </ListItemButton>

            <Collapse in={isExpanded}>
              <DistrictOrganizationList adminPortalGroup={adminPortalGroup} />
            </Collapse>
          </Fragment>
        );
      })}
    </List>
  );
};

export default OrganizationList;
